// components/InvoiceGenerator.js
import React, { useState, useRef } from 'react';
import TemplateSelector from './components/TemplateSelector';
import CompanyForm from './components/CompanyForm';
import CustomerForm from './components/CustomerForm';
import ItemsForm from './components/ItemsForm';
import InvoicePreview from './components/InvoicePreview';
import ActionButtons from './components/ActionButtons';
import InvoiceSaveLoad from './components/InvoiceSaveLoad';
import { templates } from './data/templates';
import { Plus, Building2, FileText } from 'lucide-react';

const InvoiceGenerator = () => {
  const printRef = useRef();
  
  // 상태 관리
  const [selectedTemplate, setSelectedTemplate] = useState(0);
  
  const [companyInfo, setCompanyInfo] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    businessNumber: ''
  });
  
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    address: '',
    phone: ''
  });
  
  const [invoiceInfo, setInvoiceInfo] = useState({
    invoiceNumber: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    title: '견적서'
  });
  
  const [items, setItems] = useState([
    { id: 1, description: '', unitPrice: 0, quantity: 1, amount: 0 }
  ]);
  
  const [taxRate, setTaxRate] = useState(10);
  const [notes, setNotes] = useState('');
  
  // 견적서 저장/불러오기 관련 상태
  const [showSaveLoadModal, setShowSaveLoadModal] = useState(false);
  
  // 품목 관리 함수들
  const addItem = () => {
    const newId = Math.max(...items.map(item => item.id)) + 1;
    setItems([...items, { 
      id: newId, 
      description: '', 
      unitPrice: 0, 
      quantity: 1, 
      amount: 0 
    }]);
  };
  
  const removeItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };
  
  const updateItem = (id, field, value) => {
    setItems(prevItems => prevItems.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'unitPrice' || field === 'quantity') {
          updatedItem.amount = updatedItem.unitPrice * updatedItem.quantity;
        }
        return updatedItem;
      }
      return item;
    }));
  };
  

  // 계산
  const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
  const taxAmount = subtotal * (taxRate / 100);
  const total = subtotal + taxAmount;
  
  // 액션 함수들
  const handlePrint = () => {
    window.print();
  };
  
  const handleDownload = () => {
    const printContent = printRef.current.innerHTML;
    const originalContent = document.body.innerHTML;
    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  // 견적서 데이터 전체 생성
  const getCurrentInvoiceData = () => {
    return {
      selectedTemplate,
      companyInfo,
      customerInfo,
      invoiceInfo,
      items,
      taxRate,
      notes
    };
  };

  // 견적서 불러오기
  const handleLoadInvoice = (invoiceData) => {
    setSelectedTemplate(invoiceData.selectedTemplate || 0);
    setCompanyInfo(invoiceData.companyInfo || {
      name: '',
      address: '',
      phone: '',
      email: '',
      businessNumber: ''
    });
    setCustomerInfo(invoiceData.customerInfo || {
      name: '',
      address: '',
      phone: ''
    });
    setInvoiceInfo(invoiceData.invoiceInfo || {
      invoiceNumber: '',
      date: new Date().toISOString().split('T')[0],
      dueDate: '',
      title: '견적서'
    });
    setItems(invoiceData.items || [
      { id: 1, description: '', unitPrice: 0, quantity: 1, amount: 0 }
    ]);
    setTaxRate(invoiceData.taxRate || 10);
    setNotes(invoiceData.notes || '');
  };

  // 폼 초기화 (새 견적서)
  const handleClearForm = () => {
    setSelectedTemplate(0);
    setCompanyInfo({
      name: '',
      address: '',
      phone: '',
      email: '',
      businessNumber: ''
    });
    setCustomerInfo({
      name: '',
      address: '',
      phone: ''
    });
    setInvoiceInfo({
      invoiceNumber: '',
      date: new Date().toISOString().split('T')[0],
      dueDate: '',
      title: '견적서'
    });
    setItems([
      { id: 1, description: '', unitPrice: 0, quantity: 1, amount: 0 }
    ]);
    setTaxRate(10);
    setNotes('');
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 py-20">
      <div className="max-w-7xl mx-auto">
        {/* 헤더 */}
        <div className="bg-white/50 rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">견적서 자동 생성 프로그램</h1>
            <button
              onClick={() => setShowSaveLoadModal(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
            >
              <FileText className="w-4 h-4" />
              견적서 관리
            </button>
          </div>
          
          <TemplateSelector 
            templates={templates}
            selectedTemplate={selectedTemplate}
            onTemplateSelect={setSelectedTemplate}
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* 입력 폼 */}
          <div className="xl:col-span-2 bg-white rounded-lg shadow-sm p-4 lg:p-6">
            <h2 className="text-base lg:text-lg font-semibold mb-4">견적서 정보 입력</h2>
            
            {/* 기본 정보 */}
            <div className="space-y-4 md:mb-4 lg:mb-10">
              <div>
                <label className="text-sm font-medium flex items-center mb-1">문서 유형</label>
                <select 
                  value={invoiceInfo.title} 
                  onChange={(e) => setInvoiceInfo({...invoiceInfo, title: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="견적서">견적서</option>
                  <option value="계약서">계약서</option>
                  <option value="영수증">영수증</option>
                  <option value="거래명세서">거래명세서</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium flex items-center mb-1">문서번호</label>
                  <input
                    type="text"
                    value={invoiceInfo.invoiceNumber}
                    onChange={(e) => setInvoiceInfo({...invoiceInfo, invoiceNumber: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="INV-001"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium flex items-center mb-1">작성일자</label>
                  <input
                    type="date"
                    value={invoiceInfo.date}
                    onChange={(e) => setInvoiceInfo({...invoiceInfo, date: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <CompanyForm 
              companyInfo={companyInfo}
              setCompanyInfo={setCompanyInfo}
            />
            
            <CustomerForm 
              customerInfo={customerInfo}
              setCustomerInfo={setCustomerInfo}
            />
            
            <ItemsForm 
              items={items}
              onAddItem={addItem}
              onRemoveItem={removeItem}
              onUpdateItem={updateItem}
            />

            {/* 부가세 및 비고 */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium flex items-center mb-1">부가세율 (%)</label>
                <input
                  type="number"
                  value={taxRate}
                  onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                  className="w-20 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium flex items-center mb-1">비고</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="추가 정보나 특이사항을 입력하세요"
                />
              </div>
            </div>

            <ActionButtons 
              onPrint={handlePrint}
              onDownload={handleDownload}
            />
          </div>

          {/* 미리보기 */}
          <div className="xl:col-span-2 p-4 lg:p-4">
            <h2 className="text-base lg:text-lg font-semibold mb-4">미리보기</h2>
            
            <InvoicePreview 
              ref={printRef}
              template={templates[selectedTemplate]}
              companyInfo={companyInfo}
              customerInfo={customerInfo}
              invoiceInfo={invoiceInfo}
              items={items}
              subtotal={subtotal}
              taxAmount={taxAmount}
              total={total}
              taxRate={taxRate}
              notes={notes}
            />
          </div>
        </div>

        {/* 견적서 저장/불러오기 모달 */}
        <InvoiceSaveLoad 
          invoiceData={getCurrentInvoiceData()}
          onLoadInvoice={handleLoadInvoice}
          onClearForm={handleClearForm}
          isVisible={showSaveLoadModal}
          onClose={() => setShowSaveLoadModal(false)}
        />
      </div>
    </div>
  );
};

export default InvoiceGenerator;